import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useHistory } from 'react-router-dom';
// import { useReactToPrint } from 'react-to-print';

import userRole from 'config/userRole';
import useAuth from 'hooks/useAuth';
import usePrint from 'hooks/usePrint';

import DatePicker from 'antd/es/date-picker';
import moment from 'moment';
import Divider from 'antd/es/divider';
import usePersistentDateRangePicker from 'hooks/usePersistentDateRangePicker';

import individualDetailsModel from 'state/reports/individualDetails';
// import employeeDetailsModel from 'state/reports/employeeDetails';
import settingsModel from 'state/settings/goals';
import formatWorkDate from 'views/Individuals/helpers';

import useModel from 'hooks/useModelNew';

import MainLayout from 'layouts/Main.layout';
import { Row, Col, Space } from 'components/layout';
import { Text, Title } from 'components/Text';
import PageHeader from 'components/MainPageHeader';
import FilterBar from 'components/FilterBar';
import BackButton from 'components/BackButton';
import PeriodFilter from 'components/PeriodFilter';
import Table from 'components/Table';

import IndividualPeriodStats from '../components/IndividualPeriodStats';
import StatusTag from '../components/StatusTag';
import IndividualReport from '../components/IndividualReport';

import { INDIVIDUAL_STATS_PATH } from '../constants';
import { datePickerFormats } from 'config/common';

const individualReportStyles = {
  page: `
    margin: 1cm auto;
  `,
  content: `
    background-color: white;
  `
};

const IndividualPeriodFilter = styled(PeriodFilter)`
  & .ant-tabs-nav {
    width: 100%;
  }
  & .ant-tabs-nav .ant-tabs-nav-wrap {
    display: flex;
    justify-content: center;
    transform: translateX(115px);
  }
`;

const IndividualDetails = () => {
  const { state: auth } = useAuth();
  const history = useHistory();
  const params = useParams();
  // const employeeDetails = useModel(employeeDetailsModel);
  const individualDetails = useModel(individualDetailsModel);
  const settings = useModel(settingsModel);

  const [isLoading, setIsLoading] = useState(false);
  const activeReqRef = useRef(0);
  const dateRangePicker = usePersistentDateRangePicker({ queryParam: 'dt' });
  const [printMode, setPrintMode] = useState(false);
  const [stats, setStats] = useState({
    individual: {},
    scores: {},
    timelines: [],
    loadsAndUnloads: [],
    worksBestWith: [],
    containerTypes: [],
  });

  const { ref: printContentRef, handlePrint } = usePrint({
    styles: individualReportStyles,
  });

  const settingsMap = useMemo(() => {
    if (settings.state.data) {
      return settings.state.data.reduce((acc, s) => {
        if (s.config_name === 'pct_threshold' && s.config_value > 1) {
          acc[s.config_name] = s.config_value / 100;
        } else {
          acc[s.config_name] = s.config_value;
        }
        return acc;
      }, {})
    }
    return {};
  }, [settings.state]);

  const isAgency = useMemo(() => {
    return auth.currentUser.role === userRole.AGENCY;
  }, [auth]);

  useEffect(() => {
    const id = parseInt(params.id);
    if (isNaN(id)) {
      history.replace(INDIVIDUAL_STATS_PATH);
    }

    if (history.location && history.location.state) {
      setStats(stats => ({
        ...stats,
        individual: {
          full_name: history.location.state.full_name || `Individual ${id}`,
        }
      }));
    } else {
      setStats(stats => ({
        ...stats,
        individual: {
          full_name: `Individual ${id}`,
        }
      }));
    }

  }, [params, history]);

  useEffect(() => {
    settings.actions.list();
  }, [settings.actions]);

  useEffect(() => {
    const [start, end] = dateRangePicker.dateRange;
    if (start && end) {
      const employeeId = parseInt(params.id, 10);
      if (isNaN(employeeId)) return;

      // race condition guard
      const reqId = ++activeReqRef.current;
      setIsLoading(true);
      setStats(prev => ({
        ...prev,
        data: {
          ...(prev.data || {}),
          activity: [],
        },
      }));
      individualDetails.actions.getStats(
        employeeId,
        start.format('YYYY-MM-DD'),
        end.clone().add(1, 'day').format('YYYY-MM-DD')
      ).then(data => {
        if (!data) {
          if (reqId !== activeReqRef.current) return;
          setStats(prev => ({ ...prev, data: { ...(prev.data || {}), activity: [] } }));
          return;
        }
        if (reqId !== activeReqRef.current) return;

        // const {
        //   summary,
        //   scores,
        //   timelines,
        //   unload_scores,
        //   works_best_with,
        //   container_types,
        // } = data;

        const payload = data.Payload || data.payload || data;
        const manualStats = (Array.isArray(payload?.manual_segments_stats) && payload.manual_segments_stats[0]) || {};
        const shiftSegStats = (Array.isArray(payload?.shifts_segments_stats) && payload.shifts_segments_stats[0]) || {};
        const employeeInfo = (Array.isArray(payload?.employee_details) && payload.employee_details[0]) || {};

        setStats({
          // individual: summary,
          // scores: scores.length ? scores[0] : {},
          // timelines: timelines.reduce((acc, timeline) => acc.concat(timeline), []) || [],
          // loadsAndUnloads: unload_scores,
          // worksBestWith: works_best_with || [],
          // containerTypes: container_types || [],

          individual: {
            full_name: employeeInfo.full_name || manualStats.full_name || (history?.location?.state?.full_name) || `Individual ${employeeId}`,
            code: employeeInfo.emp_code || shiftSegStats.emp_code || manualStats.emp_code || history?.location?.state?.code,
            agency: employeeInfo.agency_name || shiftSegStats.agency_name || manualStats.agency || manualStats.agency_name || history?.location?.state?.agency,
          },
          data: {
            ...payload,
            activity: Array.isArray(payload?.activity) ? payload.activity : [],
          },
        });
      })
        .finally(() => {
          if (reqId === activeReqRef.current) {
            setIsLoading(false);
          }
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // employeeDetails.actions,
    individualDetails.actions,
    dateRangePicker.dateRange,
    params.id,
  ]);

  useEffect(() => {
    if (printMode) {
      handlePrint();
      setPrintMode(false);
    }
  }, [handlePrint, printMode]);

  // const onPrint = useCallback(() => {
  //   setPrintMode(true);
  // }, []);

  return (
    <MainLayout>
      <PageHeader
        title={stats?.individual?.full_name || `Individual ${params.id}`}
        subTitle={
          <Space size={4} style={{
            position: 'absolute',
            left: '58px',
            top: '45px',
            height: '22px',
          }}>
            {stats.individual && stats.individual.code ? (
              <Text style={{ color: '#A4B0BA', fontWeight: 400, fontSize: '12px', lineHeight: '20px' }}>
                ID: {stats.individual.code}
              </Text>
            ) : null}
            {stats.individual && stats.individual.agency ? (
              <>
                <Divider type="vertical" style={{ height: '16px' }} />
                <Text style={{ color: '#A4B0BA', fontWeight: 400, fontSize: '12px', lineHeight: '20px' }}>
                  Agency: {stats.individual.agency}
                </Text>
              </>
            ) : null}
          </Space>
        }
        tags={
          stats.individual && (
            <StatusTag isWorking={!!stats.individual.status} />
          )
        }
        backIcon={
          <BackButton />
        }
        onBack={() => {
          history.goBack();
        }}
      />

      <Row style={{ padding: '32px 24px 12px 24px' }}>
        <Col span={24}>
          <IndividualPeriodFilter
            selectedPeriod={dateRangePicker.period}
            onChange={dateRangePicker.setPeriod}
            style={{
              // borderBottom: '1px solid #DAE0E6',
              width: '100%'
            }}
          />
        </Col>
      </Row>

      <Row style={{ padding: '12px 24px' }}>
        <Col span={24}>
          <FilterBar
            // title={`${capitalize(dateRangePicker.period)} Stats`}
            title="Period Summary"
            titleStyles={{ fontWeight: 600, fontSize: '26px', lineHeight: 1.35 }}
            style={{ width: '100%' }}
            size="large"
          >
            <FilterBar.Slot align="left">
              <DatePicker
                mode={dateRangePicker.mode}
                picker={dateRangePicker.mode}
                value={dateRangePicker.dateRange?.[0] || null}
                format={datePickerFormats[dateRangePicker.period]}
                onChange={date => {
                  if (date) {
                    const unit = dateRangePicker.mode === 'date' ? 'day' : dateRangePicker.mode;
                    const start = date.clone().startOf(unit);
                    const end = date.clone().endOf(unit);
                    dateRangePicker.setDateRange([start, end]);
                  }
                }}
                style={{ width: '250px' }}
                allowClear={false}
                disabledDate={(current) => {
                  if (!current) return false;
                  const unit = dateRangePicker.mode === 'date' ? 'day' : dateRangePicker.mode;
                  return current > moment().endOf(unit);
                }}
              />
            </FilterBar.Slot>
            {/* <FilterBar.Slot align="right">
              <Button
                type="outlined"
                icon={{
                  name: 'file-text',
                  size: 14,
                }}
                onClick={onPrint}
                disabled={isLoading || printMode}
              >
                Generate Report
              </Button>
            </FilterBar.Slot> */}
          </FilterBar>
        </Col>
      </Row>

      <IndividualReport
        className="report"
        ref={printContentRef}
        individual={stats.individual}
        dateRange={{
          start: dateRangePicker.dateRange[0],
          end: dateRangePicker.dateRange[1],
        }}
      >
        <Row style={{ padding: '12px 24px' }}>
          <Col span={24}>
            <IndividualPeriodStats
              {...stats}
              settings={settingsMap}
              isAgency={isAgency}
              loading={isLoading}
            />
          </Col>
        </Row>

        <Row style={{ padding: '12px 24px' }}>
          <Col span={24}>
            <Title level={3} style={{ color: '#000' }}>
              Activity
            </Title>
          </Col>
          <Col span={24}>
            <Table
              dataSource={stats.data?.activity || []}
              loading={isLoading}
              rowKey={row => [row.process_id, row.work_date, row.equipment_code, row.shift_no].filter(Boolean).join(':')}
              pagination={!printMode}
            >
              <Table.Column type="datetime" title="Work Date" dataIndex="work_date" align="left" render={(value) => formatWorkDate(value)} />
              <Table.Column type="text" title="Shift" dataIndex="shift_no" align="left" render={shiftNr => {
                if (shiftNr === undefined || shiftNr === null) return '-';
                return `${shiftNr}`;
              }} />
              <Table.Column type='link' title="Equipment code" dataIndex="equipment_code" to={({ item }) => {
                if (item?.direction === 'LOAD') return `/trailers/${item.container_id}`;
                if (item?.direction === 'UNLOAD') return `/containers/${item.container_id}`;
              }} />
              <Table.Column
                type="link"
                title="Door"
                dataIndex="display_name"
                to={({ item }) => `/doors/${item.door_id}`}
                align="left"
              />
              <Table.Column
                type="text"
                title="Process"
                dataIndex="direction"
                align="left"
                render={(direction) => {
                  if (direction === 'LOAD') return 'Outbound';
                  if (direction === 'UNLOAD') return 'Inbound';
                  return '-';
                }}
              />
              <Table.Column type="text" title="Unload Method" dataIndex="unload_method_category" align="center" />
              <Table.Column type="duration" title="Individual Duration" dataIndex="emp_duration" align="right" inHrs={true} withSecs={false} />
              {/* <Table.Column type="number" title="Process ID" dataIndex="process_id" align="right" precision={0} /> */}
              <Table.Column type="duration" title="Target Time" dataIndex="target_time" align="right" inHrs={true} withSecs={false} />
              <Table.Column type="duration" title="Unload Time" dataIndex="unload_time" align="right" inHrs={true} withSecs={false} />
              <Table.Column type="number" title="Actual CPH" dataIndex="actual_cph" precision={0} align="right" />
              <Table.Column
                type="text"
                title="Speed"
                dataIndex="speed"
                align="right"
                render={(speed) => {
                  if (!speed) return '-';
                  const key = typeof speed === 'string' ? speed.toUpperCase() : speed;
                  const color = {
                    VERY_SLOW: '#F5222D',
                    SLOW: '#FAAD14',
                    NORMAL: '#52C41A',
                    FAST: '#42B0D5',
                  }[key];
                  return (
                    <Text style={{ textTransform: 'capitalize', color }}>
                      {typeof speed === 'string' ? speed.toLowerCase() : speed}
                    </Text>
                  );
                }}
              />
              {/* <Table.Column type="number" title="Target CPH" dataIndex="target_cph" precision={0} align="right" /> */}
              {/* <Table.Column type="text" title="Method" dataIndex="unload_method_category" align="left" /> */}
              {/* <Table.Column type="number" title="Times Seen" dataIndex="times_seen" precision={0} align="right" /> */}
              {/* <Table.Column type="percentage" title="% Seen" dataIndex="pct_seen" align="right" /> */}
              {/* <Table.Column type="duration" title="Emp Duration" dataIndex="emp_duration" align="right" inHrs={true} withSecs={false} /> */}
            </Table>
          </Col>
        </Row>

        {/* <Row style={{ padding: '12px 24px 32px 24px' }}>
          <Col span={24}>
            <Title level={3} style={{ color: '#000' }}>
              Container type
            </Title>
          </Col>
          <Col span={24}>
            <Table
              dataSource={stats.containerTypes}
              loading={isLoading}
              pagination={false}
              rowKey={'container_class'}
            >
              <Table.Column title="Container" dataIndex="container_class" width="30%" />
              <Table.Column type="number" title="Average CPH" dataIndex="avg_cph" align="right" precision={0} />
              <Table.Column type="number" title="Average Target CPH" dataIndex="avg_target_cph" align="right" precision={0} />
              <Table.Column type="number" title="# of Equipments" dataIndex="tot_processes" align="right" precision={0} />
              <Table.Column type="duration" title="Saved time" dataIndex="avg_saved_mins" align="right" inMins={true} withSecs={false} />
            </Table>
          </Col>
        </Row> */}

      </IndividualReport>

    </MainLayout>

  );
};

export default IndividualDetails;